/**
 * Persistent reminder banner for users who claimed a convention coupon
 * with a conditional reward (`credits_on_subscribe` / `months_on_subscribe`)
 * that pays out only after they subscribe to a specific paid plan.
 *
 * Wire shape (verified against `autopilot-backend/routes/coupons.js`
 * GET /coupons/my):
 *   {
 *     claimed: [...],
 *     pending: [
 *       {
 *         id: string,
 *         type: 'credits_on_subscribe' | 'months_on_subscribe',
 *         payload: Reward,          // full reward object
 *         trigger: { plan: string } | null,
 *         expiresAt: string,        // ISO
 *         sourceCode: string,
 *       }, ...
 *     ],
 *     freeAccessUntil: string | null,
 *     freeAccessPlan: string | null,
 *   }
 *
 * Backend already filters `status === 'pending'` server-side so we don't
 * need to re-filter here — we only drop entries whose `expiresAt` is in
 * the past (clock-skew safe: backend will still honor a few seconds past
 * the deadline, but we hide the banner so we don't lead the user to a
 * pricing page when the reward is moot).
 *
 * Fetch policy: ONE in-flight request per auth session, cached on the
 * module so navigating between authed pages doesn't re-hit /coupons/my.
 * The cache is cleared when the user logs out (we re-read `isAuthenticated`
 * on every render and reset the promise + result on the false→true edge
 * via the auth-flip effect). No SWR / react-query — explicit per the brief.
 *
 * Failures are deliberately silent. A 5xx, 401, or network glitch must
 * never block the page underneath; we just don't render the banner.
 */
import React, { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import useAuth from '../../auth/hooks/useAuth';
import { getMyCoupons } from '../services/couponApi';
import type { MyCouponsResponse, Reward } from '../types';
import RewardSummary from './RewardSummary';

/**
 * Shape of one entry inside MyCouponsResponse.pending — exported as a
 * local alias so the helpers below have a single name to grip.
 */
type PendingEntry = MyCouponsResponse['pending'][number];

/**
 * Module-level in-flight + result cache so re-mounts (route changes)
 * inside a single auth session reuse the same fetch. Cleared from
 * inside the component on the false→true auth edge.
 */
let inflight: Promise<MyCouponsResponse> | null = null;
let cached: MyCouponsResponse | null = null;

/** Test-only: reset the module cache. Not exported from feature index. */
export function __resetPendingRewardBannerCache(): void {
  inflight = null;
  cached = null;
}

function fetchOnce(): Promise<MyCouponsResponse> {
  if (cached) return Promise.resolve(cached);
  if (inflight) return inflight;
  inflight = getMyCoupons()
    .then((res) => {
      cached = res;
      return res;
    })
    .finally(() => {
      inflight = null;
    });
  return inflight;
}

/**
 * Pretty-print an ISO date as e.g. "Sep 12". Locale-default month name +
 * day-of-month — no year, no time. We intentionally don't try to render
 * "in 3 days" copy here: the convention runs for 4 days, all `expiresAt`
 * stamps are within weeks so an explicit "Expires Sep 12" reads cleaner
 * than a relative phrase that flips polarity on the user.
 */
function formatExpiry(iso: string): string {
  const d = new Date(iso);
  // Defensive: the upstream `live()` filter already rejects any entry
  // whose date parses to NaN, so in practice formatExpiry is never
  // called with one. This branch is here purely so a future change
  // that bypasses `live()` can't crash the banner.
  /* v8 ignore next */
  if (Number.isNaN(d.getTime())) return '';
  try {
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '';
  }
}

/**
 * Pick the soonest expiry across the live entries. The banner shows
 * ONE expiry date even when multiple rewards are pending — leading with
 * the earliest deadline keeps the conversion pressure honest.
 */
function earliestExpiry(entries: PendingEntry[]): string {
  let min: number | null = null;
  let pickIso = '';
  for (const e of entries) {
    const t = new Date(e.expiresAt).getTime();
    if (Number.isFinite(t) && (min === null || t < min)) {
      min = t;
      pickIso = e.expiresAt;
    }
  }
  return pickIso;
}

/** Return only entries whose expiresAt is strictly in the future. */
function live(entries: PendingEntry[]): PendingEntry[] {
  const now = Date.now();
  return entries.filter((e) => {
    const t = new Date(e.expiresAt).getTime();
    return Number.isFinite(t) && t > now;
  });
}

const PendingRewardBanner: React.FC = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState<MyCouponsResponse | null>(cached);
  const wasAuthed = useRef<boolean>(isAuthenticated);

  useEffect(() => {
    // false→true edge: clear stale cache from a previous user session
    // and re-fetch. true→false: clear so the next sign-in starts clean.
    if (wasAuthed.current && !isAuthenticated) {
      __resetPendingRewardBannerCache();
      setData(null);
    }
    wasAuthed.current = isAuthenticated;

    if (!isAuthenticated) return;

    let cancelled = false;
    fetchOnce()
      .then((res) => {
        if (!cancelled) setData(res);
      })
      .catch(() => {
        // Silent: a failed /coupons/my must never block the page
        // underneath. The next auth-session re-mount gets a fresh try.
        if (!cancelled) setData(null);
      });
    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  if (!isAuthenticated || !data) return null;

  const livePending = live(data.pending);
  if (livePending.length === 0) return null;

  const expiry = formatExpiry(earliestExpiry(livePending));
  const expiryText = expiry ? `Expires ${expiry}.` : '';

  // Single reward: render the reward in plain English via the same
  // <RewardSummary> the redeem receipt uses. Multi-reward: aggregate
  // into one line so the banner stays one row tall.
  const isSingle = livePending.length === 1;
  const single = livePending[0];

  return (
    <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center', px: 2, pt: 1 }}>
      <Alert
        severity="info"
        variant="filled"
        data-testid="pending-reward-banner"
        sx={{
          width: '100%',
          maxWidth: 'lg',
          alignItems: 'center',
          '& .MuiAlert-message': { flexGrow: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 },
        }}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            data-testid="pending-reward-banner-cta"
            onClick={() => navigate('/pricing')}
            sx={{ whiteSpace: 'nowrap', ml: 1 }}
          >
            View pricing
          </Button>
        }
      >
        {isSingle ? (
          <Box
            component="span"
            data-testid="pending-reward-banner-single"
            sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}
          >
            <RewardSummary reward={single.payload as Reward} />
            {expiryText && (
              <Box component="span" sx={{ opacity: 0.85, fontSize: '0.9em' }}>
                {expiryText}
              </Box>
            )}
          </Box>
        ) : (
          <Box
            component="span"
            data-testid="pending-reward-banner-multi"
            sx={{ display: 'inline-flex', flexWrap: 'wrap', alignItems: 'baseline', gap: 1 }}
          >
            <Box component="span" sx={{ fontWeight: 500 }}>
              You have {livePending.length} rewards waiting on a paid plan.
            </Box>
            {expiryText && (
              <Box component="span" sx={{ opacity: 0.85, fontSize: '0.9em' }}>
                {expiryText}
              </Box>
            )}
          </Box>
        )}
      </Alert>
    </Box>
  );
};

export default PendingRewardBanner;
